import React, { useState, useEffect } from 'react';
import { useFarm } from '../../contexts/FarmContext';
import { shopAPI, farmAPI } from '../../services/api';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import toast from 'react-hot-toast';
import {
  Cog6ToothIcon,
  ShoppingBagIcon,
  ClipboardDocumentListIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon,
  LinkIcon,
  QrCodeIcon,
  CheckCircleIcon,
  ClockIcon,
  XCircleIcon,
  ShareIcon,
  PhotoIcon
} from '@heroicons/react/24/outline';

const Shop = () => {
  const { currentFarm } = useFarm();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('settings');
  const [shop, setShop] = useState(null);
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [cohorts, setCohorts] = useState([]);
  const [showProductModal, setShowProductModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (currentFarm) {
      loadShopData();
    }
  }, [currentFarm]);

  const loadShopData = async () => {
    try {
      setLoading(true);
      const [shopRes, productsRes, cohortsRes] = await Promise.all([
        shopAPI.getSettings(currentFarm.id),
        shopAPI.listProducts(currentFarm.id),
        farmAPI.listCohorts(currentFarm.id)
      ]);
      setShop(shopRes.data.shop);
      setProducts(productsRes.data.products || []);
      setCohorts(cohortsRes.data.cohorts || []);
    } catch (error) {
      console.error('Load shop data error:', error);
      toast.error('Failed to load shop data');
    } finally {
      setLoading(false);
    }
  };

  const loadOrders = async () => {
    try {
      const res = await shopAPI.listOrders(currentFarm.id);
      setOrders(res.data.orders || []);
    } catch (error) {
      console.error('Load orders error:', error);
    }
  };

  const loadCustomers = async () => {
    try {
      const res = await shopAPI.listCustomers(currentFarm.id);
      setCustomers(res.data.customers || []);
    } catch (error) {
      console.error('Load customers error:', error);
    }
  };

  useEffect(() => {
    if (currentFarm && activeTab === 'orders') loadOrders();
    if (currentFarm && activeTab === 'customers') loadCustomers();
  }, [currentFarm, activeTab]);

  if (!currentFarm) {
    return (
      <div className="text-center py-12">
        <ShoppingBagIcon className="mx-auto h-12 w-12 text-gray-400" />
        <h3 className="mt-2 text-sm font-medium text-gray-900">Select a farm</h3>
        <p className="mt-1 text-sm text-gray-500">Choose a farm to manage its shop.</p>
      </div>
    );
  }

  if (loading) {
    return <div className="flex justify-center py-12"><LoadingSpinner size="lg" /></div>;
  }

  const tabs = [
    { id: 'settings', name: 'Settings', icon: Cog6ToothIcon },
    { id: 'products', name: 'Products', icon: ShoppingBagIcon },
    { id: 'orders', name: 'Orders', icon: ClipboardDocumentListIcon }
  ];

  const shopUrl = shop?.slug ? `${window.location.origin}/order/${shop.slug}` : null;

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="relative overflow-hidden bg-gradient-to-br from-green-600 via-emerald-600 to-teal-700 rounded-2xl p-6 sm:p-8 text-white">
        <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2"></div>
        <div className="absolute bottom-0 left-0 w-40 h-40 bg-white/5 rounded-full translate-y-1/2 -translate-x-1/2"></div>
        
        <div className="relative flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-white/20 backdrop-blur rounded-xl">
              <ShoppingBagIcon className="h-8 w-8" />
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold">Online Shop</h1>
              <p className="text-green-100">Manage your farm's online ordering system</p>
            </div>
          </div>
          {shopUrl && shop?.isActive && (
            <div className="flex items-center gap-2">
              <a
                href={shopUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center px-4 py-2 bg-white/20 hover:bg-white/30 rounded-xl transition-colors text-sm"
              >
                <LinkIcon className="h-4 w-4 mr-1.5" />
                View Shop
              </a>
              <button
                onClick={() => {
                  navigator.clipboard.writeText(shopUrl);
                  toast.success('Shop link copied!');
                }}
                className="inline-flex items-center px-4 py-2 bg-white text-green-700 rounded-xl hover:bg-green-50 transition-colors text-sm font-medium"
              >
                <ShareIcon className="h-4 w-4 mr-1.5" />
                Copy Link
              </button>
            </div>
          )}
        </div>
        
        {/* Stats */}
        <div className="relative mt-6 grid grid-cols-3 gap-3">
          <div className="bg-white/10 backdrop-blur rounded-xl p-3 text-center">
            <p className="text-2xl font-bold">{products.length}</p>
            <p className="text-green-200 text-xs">Products</p>
          </div>
          <div className="bg-white/10 backdrop-blur rounded-xl p-3 text-center">
            <p className="text-2xl font-bold">{orders.length}</p>
            <p className="text-green-200 text-xs">Orders</p>
          </div>
          <div className="bg-white/10 backdrop-blur rounded-xl p-3 text-center">
            <p className="text-2xl font-bold">{shop?.isActive ? 'Active' : 'Inactive'}</p>
            <p className="text-green-200 text-xs">Status</p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <nav className="flex border-b border-gray-100 overflow-x-auto">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center py-4 px-4 sm:px-6 font-medium text-sm whitespace-nowrap flex-shrink-0 transition-colors ${
                activeTab === tab.id
                  ? 'text-green-600 border-b-2 border-green-500 bg-green-50'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <tab.icon className="h-5 w-5 mr-2" />
              {tab.name}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'settings' && (
        <SettingsTab shop={shop} setShop={setShop} farmId={currentFarm.id} />
      )}

      {activeTab === 'products' && (
        <ProductsTab
          products={products}
          setProducts={setProducts}
          cohorts={cohorts}
          farmId={currentFarm.id}
          showModal={showProductModal}
          setShowModal={setShowProductModal}
          editingProduct={editingProduct}
          setEditingProduct={setEditingProduct}
        />
      )}

      {activeTab === 'orders' && (
        <OrdersTab orders={orders} setOrders={setOrders} farmId={currentFarm.id} onRefresh={loadOrders} />
      )}
    </div>
  );
};

// Settings Tab Component
const SettingsTab = ({ shop, setShop, farmId }) => {
  const [form, setForm] = useState({
    shopName: '',
    description: '',
    contactPhone: '',
    contactWhatsapp: '',
    settlementMethod: 'mpesa',
    settlementPhone: '',
    settlementBankName: '',
    settlementBankAccount: '',
    settlementBankBranch: '',
    settlementInterval: 'weekly',
    isActive: false,
    acceptingOrders: true,
    deliveryAvailable: true,
    pickupAvailable: true,
    deliveryFee: 0,
    pickupAddress: '',
    minimumOrderAmount: ''
  });
  const [saving, setSaving] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [shopImage, setShopImage] = useState(null);
  const imageInputRef = React.useRef(null);

  useEffect(() => {
    if (shop) {
      setForm({
        shopName: shop.shopName || '',
        description: shop.description || '',
        contactPhone: shop.contactPhone || '',
        contactWhatsapp: shop.contactWhatsapp || '',
        settlementMethod: shop.settlementMethod || 'mpesa',
        settlementPhone: shop.settlementPhone || '',
        settlementBankName: shop.settlementBankName || '',
        settlementBankAccount: shop.settlementBankAccount || '',
        settlementBankBranch: shop.settlementBankBranch || '',
        settlementInterval: shop.settlementInterval || 'weekly',
        isActive: shop.isActive || false,
        acceptingOrders: shop.acceptingOrders !== false,
        deliveryAvailable: shop.deliveryAvailable !== false,
        pickupAvailable: shop.pickupAvailable !== false,
        deliveryFee: shop.deliveryFee || 0,
        pickupAddress: shop.pickupAddress || '',
        minimumOrderAmount: shop.minimumOrderAmount || ''
      });
      setShopImage(shop.imageUrl || null);
    }
  }, [shop]);

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }
    
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be less than 5MB');
      return;
    }
    
    try {
      setUploadingImage(true);
      const res = await shopAPI.uploadShopImage(farmId, file);
      setShopImage(res.data.url);
      if (res.data.shop) {
        setShop(res.data.shop);
      }
      toast.success('Shop image uploaded!');
    } catch (error) {
      console.error('Image upload error:', error);
      toast.error(error.response?.data?.message || 'Failed to upload image');
    } finally {
      setUploadingImage(false);
      if (imageInputRef.current) {
        imageInputRef.current.value = '';
      }
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      // Clean up form data - convert empty strings to null for numeric fields
      const cleanedData = {
        ...form,
        minimumOrderAmount: form.minimumOrderAmount === '' ? null : Number(form.minimumOrderAmount) || null,
        deliveryFee: form.deliveryFee === '' ? null : Number(form.deliveryFee) || 0,
        // Ensure empty strings become null for optional text fields
        description: form.description || null,
        contactPhone: form.contactPhone || null,
        contactWhatsapp: form.contactWhatsapp || null,
        settlementPhone: form.settlementPhone || null,
        settlementBankName: form.settlementBankName || null,
        settlementBankAccount: form.settlementBankAccount || null,
        settlementBankBranch: form.settlementBankBranch || null,
        pickupAddress: form.pickupAddress || null,
      };
      const res = await shopAPI.updateSettings(farmId, cleanedData);
      setShop(res.data.shop);
      toast.success('Shop settings saved!');
    } catch (error) {
      console.error('Save settings error:', error);
      toast.error(error.response?.data?.message || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const shopUrl = shop?.slug ? `${window.location.origin}/order/${shop.slug}` : null;

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Shop URL Card */}
      {shopUrl && (
        <div className="bg-gradient-to-r from-primary-50 to-blue-50 border border-primary-200 rounded-xl p-4 sm:p-6">
          <h3 className="font-semibold text-gray-900 mb-2 text-sm sm:text-base">Your Shop Link</h3>
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3 bg-white rounded-lg p-2 sm:p-3 border">
            <input
              type="text"
              readOnly
              value={shopUrl}
              className="flex-1 bg-transparent border-none text-xs sm:text-sm text-gray-700 focus:outline-none truncate"
            />
            <button
              onClick={() => {
                navigator.clipboard.writeText(shopUrl);
                toast.success('Link copied!');
              }}
              className="px-3 py-2 sm:py-1.5 bg-primary-600 text-white text-sm rounded-lg hover:bg-primary-700 w-full sm:w-auto"
            >
              Copy
            </button>
          </div>
          <p className="text-xs sm:text-sm text-gray-600 mt-2">
            Share this link with your customers so they can order directly from you.
          </p>
        </div>
      )}

      {/* Shop Banner Image */}
      <div className="bg-white rounded-xl border p-4 sm:p-6">
        <h3 className="font-semibold text-gray-900 mb-3 sm:mb-4 text-sm sm:text-base">Shop Banner Image</h3>
        <div className="space-y-4">
          {/* Image Preview */}
          <div className="relative w-full h-40 sm:h-48 bg-gradient-to-br from-primary-50 to-amber-50 rounded-lg overflow-hidden border-2 border-dashed border-gray-300">
            {shopImage ? (
              <>
                <img
                  src={shopImage}
                  alt="Shop banner"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-black/40 opacity-0 hover:opacity-100 transition-opacity flex items-center justify-center">
                  <button
                    onClick={() => imageInputRef.current?.click()}
                    disabled={uploadingImage}
                    className="px-4 py-2 bg-white text-gray-900 rounded-lg font-medium text-sm hover:bg-gray-100"
                  >
                    Change Image
                  </button>
                </div>
              </>
            ) : (
              <div
                onClick={() => !uploadingImage && imageInputRef.current?.click()}
                className="w-full h-full flex flex-col items-center justify-center cursor-pointer hover:bg-gray-50 transition-colors"
              >
                {uploadingImage ? (
                  <>
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mb-2"></div>
                    <p className="text-sm text-gray-500">Uploading...</p>
                  </>
                ) : (
                  <>
                    <PhotoIcon className="h-12 w-12 text-gray-400 mb-2" />
                    <p className="text-sm text-gray-500 font-medium">Click to upload shop banner</p>
                    <p className="text-xs text-gray-400 mt-1">Recommended: 1200 x 400 pixels</p>
                  </>
                )}
              </div>
            )}
          </div>
          <input
            ref={imageInputRef}
            type="file"
            accept="image/*"
            onChange={handleImageUpload}
            className="hidden"
          />
          <p className="text-xs text-gray-500">
            This image will be displayed as the banner on your public shop page. Use a high-quality image that represents your farm.
          </p>
        </div>
      </div>

      {/* Settings Form */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
        {/* Basic Info */}
        <div className="bg-white rounded-xl border p-4 sm:p-6">
          <h3 className="font-semibold text-gray-900 mb-3 sm:mb-4 text-sm sm:text-base">Shop Information</h3>
          <div className="space-y-3 sm:space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Shop Name</label>
              <input
                type="text"
                value={form.shopName}
                onChange={(e) => setForm({ ...form, shopName: e.target.value })}
                className="input w-full"
                placeholder="e.g., Kimani's Fresh Eggs"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
              <textarea
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                className="input w-full"
                rows={3}
                placeholder="Describe your farm and products..."
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Contact Phone</label>
              <input
                type="tel"
                value={form.contactPhone}
                onChange={(e) => setForm({ ...form, contactPhone: e.target.value })}
                className="input w-full"
                placeholder="0712 345 678"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">WhatsApp Number</label>
              <input
                type="tel"
                value={form.contactWhatsapp}
                onChange={(e) => setForm({ ...form, contactWhatsapp: e.target.value })}
                className="input w-full"
                placeholder="0712 345 678"
              />
            </div>
          </div>
        </div>

        {/* Settlement Settings */}
        <div className="bg-white rounded-xl border p-4 sm:p-6">
          <h3 className="font-semibold text-gray-900 mb-2 sm:mb-4 text-sm sm:text-base">Settlement Settings</h3>
          <p className="text-xs sm:text-sm text-gray-600 mb-3 sm:mb-4">How you'll receive payments from your sales.</p>
          <div className="space-y-3 sm:space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Settlement Method</label>
              <select
                value={form.settlementMethod}
                onChange={(e) => setForm({ ...form, settlementMethod: e.target.value })}
                className="input w-full"
              >
                <option value="mpesa">M-Pesa</option>
                <option value="bank">Bank Transfer</option>
              </select>
            </div>

            {form.settlementMethod === 'mpesa' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">M-Pesa Phone Number</label>
                <input
                  type="tel"
                  value={form.settlementPhone}
                  onChange={(e) => setForm({ ...form, settlementPhone: e.target.value })}
                  className="input w-full"
                  placeholder="0712 345 678"
                />
              </div>
            )}

            {form.settlementMethod === 'bank' && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Bank Name</label>
                  <input
                    type="text"
                    value={form.settlementBankName}
                    onChange={(e) => setForm({ ...form, settlementBankName: e.target.value })}
                    className="input w-full"
                    placeholder="e.g., Equity Bank"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Account Number</label>
                  <input
                    type="text"
                    value={form.settlementBankAccount}
                    onChange={(e) => setForm({ ...form, settlementBankAccount: e.target.value })}
                    className="input w-full"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Branch</label>
                  <input
                    type="text"
                    value={form.settlementBankBranch}
                    onChange={(e) => setForm({ ...form, settlementBankBranch: e.target.value })}
                    className="input w-full"
                  />
                </div>
              </>
            )}

          </div>
        </div>

        {/* Delivery & Pickup */}
        <div className="bg-white rounded-xl border p-4 sm:p-6">
          <h3 className="font-semibold text-gray-900 mb-3 sm:mb-4 text-sm sm:text-base">Delivery & Pickup</h3>
          <div className="space-y-3 sm:space-y-4">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={form.deliveryAvailable}
                onChange={(e) => setForm({ ...form, deliveryAvailable: e.target.checked })}
                className="rounded border-gray-300 text-primary-600 mr-2"
              />
              <span className="text-sm text-gray-700">Offer delivery</span>
            </label>
            
            {form.deliveryAvailable && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Delivery Fee (KES)</label>
                <input
                  type="number"
                  value={form.deliveryFee}
                  onChange={(e) => setForm({ ...form, deliveryFee: e.target.value })}
                  className="input w-full"
                  min="0"
                />
              </div>
            )}

            <label className="flex items-center">
              <input
                type="checkbox"
                checked={form.pickupAvailable}
                onChange={(e) => setForm({ ...form, pickupAvailable: e.target.checked })}
                className="rounded border-gray-300 text-primary-600 mr-2"
              />
              <span className="text-sm text-gray-700">Offer pickup</span>
            </label>

            {form.pickupAvailable && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Pickup Address</label>
                <textarea
                  value={form.pickupAddress}
                  onChange={(e) => setForm({ ...form, pickupAddress: e.target.value })}
                  className="input w-full"
                  rows={2}
                  placeholder="Where customers can pick up orders"
                />
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Minimum Order (KES)</label>
              <input
                type="number"
                value={form.minimumOrderAmount}
                onChange={(e) => setForm({ ...form, minimumOrderAmount: e.target.value })}
                className="input w-full"
                min="0"
                placeholder="Optional"
              />
            </div>
          </div>
        </div>

        {/* Shop Status */}
        <div className="bg-white rounded-xl border p-4 sm:p-6">
          <h3 className="font-semibold text-gray-900 mb-3 sm:mb-4 text-sm sm:text-base">Shop Status</h3>
          <div className="space-y-3 sm:space-y-4">
            <label className="flex items-center justify-between p-3 sm:p-4 bg-gray-50 rounded-lg cursor-pointer">
              <div className="flex-1 mr-3">
                <span className="font-medium text-gray-900 text-sm sm:text-base">Shop Active</span>
                <p className="text-xs sm:text-sm text-gray-500">Make your shop visible to customers</p>
              </div>
              <input
                type="checkbox"
                checked={form.isActive}
                onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
                className="h-5 w-5 rounded border-gray-300 text-primary-600 flex-shrink-0"
              />
            </label>

            <label className="flex items-center justify-between p-3 sm:p-4 bg-gray-50 rounded-lg cursor-pointer">
              <div className="flex-1 mr-3">
                <span className="font-medium text-gray-900 text-sm sm:text-base">Accepting Orders</span>
                <p className="text-xs sm:text-sm text-gray-500">Allow customers to place new orders</p>
              </div>
              <input
                type="checkbox"
                checked={form.acceptingOrders}
                onChange={(e) => setForm({ ...form, acceptingOrders: e.target.checked })}
                className="h-5 w-5 rounded border-gray-300 text-primary-600 flex-shrink-0"
              />
            </label>
          </div>
        </div>
      </div>

      {/* Save Button - Fixed on mobile */}
      <div className="flex justify-end sticky bottom-4 sm:static">
        <button
          onClick={handleSave}
          disabled={saving}
          className="btn-primary w-full sm:w-auto shadow-lg sm:shadow-none"
        >
          {saving ? 'Saving...' : 'Save Settings'}
        </button>
      </div>
    </div>
  );
};

// Products Tab Component
const ProductsTab = ({ products, setProducts, cohorts, farmId, showModal, setShowModal, editingProduct, setEditingProduct }) => {
  const [form, setForm] = useState({
    name: '',
    description: '',
    category: 'eggs',
    price: '',
    unit: 'tray',
    unitsPerPack: 30,
    stockQuantity: '',
    cohortId: '',
    imageUrl: '',
    isActive: true
  });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (editingProduct) {
      setForm({
        name: editingProduct.name || '',
        description: editingProduct.description || '',
        category: editingProduct.category || 'eggs',
        price: editingProduct.price || '',
        unit: editingProduct.unit || 'piece',
        unitsPerPack: editingProduct.unitsPerPack || '',
        stockQuantity: editingProduct.stockQuantity || '',
        cohortId: editingProduct.cohortId || '',
        imageUrl: editingProduct.imageUrl || '',
        isActive: editingProduct.isActive !== false
      });
    } else {
      setForm({
        name: '',
        description: '',
        category: 'eggs',
        price: '',
        unit: 'tray',
        unitsPerPack: 30,
        stockQuantity: '',
        cohortId: '',
        imageUrl: '',
        isActive: true
      });
    }
  }, [editingProduct]);

  const handleImageUpload = async (file) => {
    if (!file) return;
    try {
      setUploading(true);
      const res = await shopAPI.uploadProductImage(farmId, file);
      setForm(prev => ({ ...prev, imageUrl: res.data.url }));
      toast.success('Image uploaded!');
    } catch (error) {
      console.error('Image upload error:', error);
      toast.error(error.response?.data?.message || 'Failed to upload image');
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      // Clean up form data - convert empty strings to null for numeric/optional fields
      const cleanedForm = {
        ...form,
        price: form.price === '' ? null : Number(form.price),
        unitsPerPack: form.unitsPerPack === '' ? null : Number(form.unitsPerPack) || null,
        stockQuantity: form.stockQuantity === '' ? null : Number(form.stockQuantity) || null,
        cohortId: form.cohortId || null,
        description: form.description || null,
        imageUrl: form.imageUrl || null,
      };
      
      if (editingProduct) {
        const res = await shopAPI.updateProduct(farmId, editingProduct.id, cleanedForm);
        setProducts(products.map(p => p.id === editingProduct.id ? res.data.product : p));
        toast.success('Product updated!');
      } else {
        const res = await shopAPI.createProduct(farmId, cleanedForm);
        setProducts([...products, res.data.product]);
        toast.success('Product created!');
      }
      setShowModal(false);
      setEditingProduct(null);
    } catch (error) {
      console.error('Save product error:', error);
      toast.error(error.response?.data?.message || 'Failed to save product');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (product) => {
    if (!window.confirm(`Delete "${product.name}"?`)) return;
    try {
      await shopAPI.deleteProduct(farmId, product.id);
      setProducts(products.filter(p => p.id !== product.id));
      toast.success('Product deleted');
    } catch (error) {
      toast.error('Failed to delete product');
    }
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
        <h3 className="text-base sm:text-lg font-medium text-gray-900">Your Products</h3>
        <button
          onClick={() => { setEditingProduct(null); setShowModal(true); }}
          className="btn-primary w-full sm:w-auto justify-center"
        >
          <PlusIcon className="h-4 w-4 mr-1" />
          Add Product
        </button>
      </div>

      {products.length === 0 ? (
        <div className="text-center py-8 sm:py-12 bg-gray-50 rounded-xl">
          <ShoppingBagIcon className="mx-auto h-10 w-10 sm:h-12 sm:w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No products yet</h3>
          <p className="mt-1 text-xs sm:text-sm text-gray-500">Add products for customers to order.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
          {products.map((product) => (
            <div key={product.id} className={`bg-white rounded-xl border overflow-hidden ${!product.isActive ? 'opacity-60' : ''}`}>
              {/* Product Image */}
              {product.imageUrl ? (
                <div className="relative h-32 sm:h-40 bg-gray-100">
                  <img 
                    src={product.imageUrl} 
                    alt={product.name} 
                    className="w-full h-full object-cover"
                  />
                  <span className={`absolute top-2 right-2 px-2 py-0.5 text-xs rounded-full ${product.isActive ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                    {product.isActive ? 'Active' : 'Inactive'}
                  </span>
                </div>
              ) : (
                <div className="h-20 sm:h-24 bg-gradient-to-br from-gray-100 to-gray-50 flex items-center justify-center">
                  <ShoppingBagIcon className="h-8 w-8 text-gray-300" />
                </div>
              )}
              <div className="p-3 sm:p-4">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <h4 className="font-medium text-gray-900 text-sm sm:text-base truncate">{product.name}</h4>
                    <p className="text-xs sm:text-sm text-gray-500 capitalize">{product.category}</p>
                  </div>
                  {!product.imageUrl && (
                    <span className={`px-2 py-0.5 sm:py-1 text-xs rounded-full flex-shrink-0 ${product.isActive ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                      {product.isActive ? 'Active' : 'Inactive'}
                    </span>
                  )}
                </div>
                <div className="mt-2 sm:mt-3">
                  <span className="text-xl sm:text-2xl font-bold text-gray-900">KES {product.price}</span>
                  <span className="text-xs sm:text-sm text-gray-500 ml-1">/ {product.unit}</span>
                </div>
                {product.cohort && (
                  <p className="text-xs text-gray-500 mt-1">Flock: {product.cohort.name}</p>
                )}
                <div className="mt-3 sm:mt-4 flex gap-2">
                  <button
                    onClick={() => { setEditingProduct(product); setShowModal(true); }}
                    className="flex-1 px-3 py-2 sm:py-1.5 text-xs sm:text-sm border rounded-lg hover:bg-gray-50 flex items-center justify-center"
                  >
                    <PencilIcon className="h-3.5 w-3.5 sm:h-4 sm:w-4 mr-1" />
                    Edit
                  </button>
                  <button
                    onClick={() => handleDelete(product)}
                    className="px-3 py-2 sm:py-1.5 text-xs sm:text-sm border border-red-200 text-red-600 rounded-lg hover:bg-red-50 flex items-center justify-center"
                  >
                    <TrashIcon className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Product Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4">
          <div className="bg-white rounded-t-2xl sm:rounded-xl w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b px-4 py-3 flex items-center justify-between sm:hidden">
              <h3 className="text-base font-semibold">
                {editingProduct ? 'Edit Product' : 'Add Product'}
              </h3>
              <button onClick={() => { setShowModal(false); setEditingProduct(null); }} className="text-gray-500">
                <XCircleIcon className="h-6 w-6" />
              </button>
            </div>
            <div className="p-4 sm:p-6">
              <h3 className="text-lg font-semibold mb-4 hidden sm:block">
                {editingProduct ? 'Edit Product' : 'Add Product'}
              </h3>
              <div className="space-y-3 sm:space-y-4">
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Product Name</label>
                  <input
                    type="text"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="input w-full text-sm"
                    placeholder="e.g., Egg Tray"
                  />
                </div>
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Category</label>
                  <select
                    value={form.category}
                    onChange={(e) => setForm({ ...form, category: e.target.value })}
                    className="input w-full text-sm"
                  >
                    <option value="eggs">Eggs</option>
                    <option value="chicken">Chicken</option>
                    <option value="chicks">Chicks</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                <div className="grid grid-cols-2 gap-3 sm:gap-4">
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Price (KES)</label>
                    <input
                      type="number"
                      value={form.price}
                      onChange={(e) => setForm({ ...form, price: e.target.value })}
                      className="input w-full text-sm"
                      min="0"
                    />
                  </div>
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Unit</label>
                    <select
                      value={form.unit}
                      onChange={(e) => setForm({ ...form, unit: e.target.value })}
                      className="input w-full text-sm"
                    >
                      <option value="tray">Tray</option>
                      <option value="piece">Piece</option>
                      <option value="dozen">Dozen</option>
                      <option value="kg">Kg</option>
                    </select>
                  </div>
                </div>
                {form.category === 'eggs' && form.unit === 'tray' && (
                  <div>
                    <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Eggs per Tray</label>
                    <input
                      type="number"
                      value={form.unitsPerPack}
                      onChange={(e) => setForm({ ...form, unitsPerPack: e.target.value })}
                      className="input w-full text-sm"
                      min="1"
                    />
                  </div>
                )}
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Link to Flock (Optional)</label>
                  <select
                    value={form.cohortId}
                    onChange={(e) => setForm({ ...form, cohortId: e.target.value })}
                    className="input w-full text-sm"
                  >
                    <option value="">No link</option>
                    {cohorts.map(c => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">Description</label>
                  <textarea
                    value={form.description}
                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                    className="input w-full text-sm"
                    rows={2}
                  />
                </div>
                {/* Image Upload */}
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                    Product Image <span className="text-gray-400 font-normal">(optional)</span>
                  </label>
                  {form.imageUrl ? (
                    <div className="relative">
                      <img 
                        src={form.imageUrl} 
                        alt="Product" 
                        className="w-full h-32 sm:h-40 object-cover rounded-lg border"
                      />
                      <button
                        type="button"
                        onClick={() => setForm({ ...form, imageUrl: '' })}
                        className="absolute top-2 right-2 p-1.5 bg-red-500 text-white rounded-full hover:bg-red-600 shadow-md"
                      >
                        <XCircleIcon className="h-4 w-4" />
                      </button>
                    </div>
                  ) : (
                    <label className={`flex flex-col items-center justify-center w-full h-28 sm:h-32 border-2 border-dashed rounded-lg cursor-pointer transition ${uploading ? 'border-primary-300 bg-primary-50' : 'border-gray-300 hover:border-primary-400 hover:bg-gray-50'}`}>
                      <div className="flex flex-col items-center justify-center py-4">
                        {uploading ? (
                          <>
                            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600 mb-2"></div>
                            <span className="text-xs sm:text-sm text-gray-500">Uploading...</span>
                          </>
                        ) : (
                          <>
                            <PlusIcon className="h-8 w-8 text-gray-400 mb-1" />
                            <span className="text-xs sm:text-sm text-gray-500">Click to upload image</span>
                            <span className="text-xs text-gray-400 mt-0.5">Max 5MB</span>
                          </>
                        )}
                      </div>
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        disabled={uploading}
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) handleImageUpload(file);
                          e.target.value = '';
                        }}
                      />
                    </label>
                  )}
                </div>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={form.isActive}
                    onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
                    className="rounded border-gray-300 text-primary-600 mr-2"
                  />
                  <span className="text-xs sm:text-sm text-gray-700">Product is active</span>
                </label>
              </div>
              <div className="flex gap-3 mt-5 sm:mt-6">
                <button
                  onClick={() => { setShowModal(false); setEditingProduct(null); }}
                  className="flex-1 btn-outline text-sm"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  disabled={saving || !form.name || !form.price}
                  className="flex-1 btn-primary text-sm"
                >
                  {saving ? 'Saving...' : 'Save'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Orders Tab Component
const OrdersTab = ({ orders, setOrders, farmId, onRefresh }) => {
  // Helper to determine if order is a guest order
  // Guest orders: isGuestOrder is explicitly true, OR isGuestOrder is undefined (not set by backend)
  // This ensures orders without customerEmail or without the flag are treated as guest orders
  const isGuestOrder = (order) => {
    // If explicitly set by backend, use that value
    if (order.isGuestOrder !== undefined) {
      return order.isGuestOrder;
    }
    // Fallback: if no customerEmail, treat as guest
    return !order.customerEmail;
  };

  // Filters state
  const [filters, setFilters] = useState({
    orderNumber: '',
    customer: '',
    paymentStatus: '',
    orderStatus: '',
    dateFrom: '',
    dateTo: ''
  });
  // Pagination state
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // Apply filters
  const filteredOrders = orders.filter(o => {
    if (filters.orderNumber && !o.orderNumber?.toLowerCase().includes(filters.orderNumber.toLowerCase())) return false;
    if (filters.customer) {
      const customerStr = `${o.customerName || ''} ${o.customerPhone || ''}`.toLowerCase();
      if (!customerStr.includes(filters.customer.toLowerCase())) return false;
    }
    if (filters.paymentStatus && o.paymentStatus !== filters.paymentStatus) return false;
    if (filters.orderStatus && o.status !== filters.orderStatus) return false;
    if (filters.dateFrom) {
      const orderDate = new Date(o.createdAt).toISOString().slice(0, 10);
      if (orderDate < filters.dateFrom) return false;
    }
    if (filters.dateTo) {
      const orderDate = new Date(o.createdAt).toISOString().slice(0, 10);
      if (orderDate > filters.dateTo) return false;
    }
    return true;
  });

  // Pagination
  const totalPages = Math.ceil(filteredOrders.length / pageSize);
  const pagedOrders = filteredOrders.slice((page - 1) * pageSize, page * pageSize);

  // Reset page when filters change
  const updateFilter = (key, value) => {
    setFilters(f => ({ ...f, [key]: value }));
    setPage(1);
  };

  const clearFilters = () => {
    setFilters({ orderNumber: '', customer: '', paymentStatus: '', orderStatus: '', dateFrom: '', dateTo: '' });
    setPage(1);
  };

  const hasFilters = Object.values(filters).some(v => v);

  // Note: handleMarkPaid removed - payments are processed automatically via M-Pesa callback
  // Farmers don't manually mark orders as paid since money goes to Omabra paybill

  const handleUpdateStatus = async (order, status) => {
    // Prevent shipping unpaid orders
    if (status === 'shipped' && order.paymentStatus !== 'paid') {
      toast.error('Cannot ship order - waiting for customer payment. Please wait for payment to be confirmed before shipping.');
      return;
    }
    
    try {
      await shopAPI.updateOrderStatus(farmId, order.id, status);
      toast.success('Order status updated! Customer has been notified.');
      // Optimistically update local state for instant UI feedback
      setOrders(prevOrders => 
        prevOrders.map(o => o.id === order.id ? { ...o, status, deliveredAt: status === 'delivered' ? new Date().toISOString() : o.deliveredAt } : o)
      );
      // Delay refresh to allow optimistic update to be visible first
      setTimeout(() => onRefresh(), 1500);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update order');
    }
  };

  const handleCancelOrder = async (order) => {
    const reason = window.prompt('Enter cancellation reason (optional):');
    if (reason === null) return; // User clicked cancel on prompt
    
    try {
      await shopAPI.cancelOrder(farmId, order.id, reason);
      toast.success('Order cancelled! Customer has been notified.');
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to cancel order');
    }
  };

  const handleDeleteOrder = async (order) => {
    if (!window.confirm(`Delete order #${order.orderNumber}? This cannot be undone.`)) return;
    
    try {
      await shopAPI.deleteOrder(farmId, order.id);
      toast.success('Order deleted');
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to delete order');
    }
  };

  const statusColors = {
    pending: 'bg-yellow-100 text-yellow-700',
    confirmed: 'bg-blue-100 text-blue-700',
    preparing: 'bg-purple-100 text-purple-700',
    ready: 'bg-green-100 text-green-700',
    shipped: 'bg-blue-100 text-blue-700',
    delivered: 'bg-gray-100 text-gray-700',
    cancelled: 'bg-red-100 text-red-700'
  };

  const paymentColors = {
    pending: 'bg-yellow-100 text-yellow-700',
    paid: 'bg-green-100 text-green-700',
    failed: 'bg-red-100 text-red-700'
  };

  return (
    <div className="space-y-3 sm:space-y-4">
      {/* Filters Row */}
      <div className="bg-white rounded-xl border p-3 sm:p-4">
        <div className="flex flex-wrap items-center gap-2 sm:gap-3">
          <input
            type="text"
            placeholder="Order #"
            className="input py-1.5 text-sm w-24 sm:w-28"
            value={filters.orderNumber}
            onChange={(e) => updateFilter('orderNumber', e.target.value)}
          />
          <input
            type="text"
            placeholder="Customer..."
            className="input py-1.5 text-sm w-32 sm:w-36"
            value={filters.customer}
            onChange={(e) => updateFilter('customer', e.target.value)}
          />
          <select
            className="input py-1.5 text-sm"
            value={filters.paymentStatus}
            onChange={(e) => updateFilter('paymentStatus', e.target.value)}
          >
            <option value="">All Payments</option>
            <option value="pending">⏳ Pending</option>
            <option value="paid">✓ Paid</option>
            <option value="failed">✗ Failed</option>
          </select>
          <select
            className="input py-1.5 text-sm"
            value={filters.orderStatus}
            onChange={(e) => updateFilter('orderStatus', e.target.value)}
          >
            <option value="">All Status</option>
            <option value="pending">Pending</option>
            <option value="confirmed">Confirmed</option>
            <option value="preparing">Preparing</option>
            <option value="ready">Ready</option>
            <option value="shipped">Shipped</option>
            <option value="delivered">Delivered</option>
            <option value="cancelled">Cancelled</option>
          </select>
          <div className="flex items-center gap-1">
            <input
              type="date"
              className="input py-1.5 text-sm"
              value={filters.dateFrom}
              onChange={(e) => updateFilter('dateFrom', e.target.value)}
              title="From date"
            />
            <span className="text-gray-400">-</span>
            <input
              type="date"
              className="input py-1.5 text-sm"
              value={filters.dateTo}
              onChange={(e) => updateFilter('dateTo', e.target.value)}
              title="To date"
            />
          </div>
          {hasFilters && (
            <button
              onClick={clearFilters}
              className="text-sm text-gray-500 hover:text-gray-700 underline"
            >
              Clear
            </button>
          )}
          <div className="ml-auto">
            <button onClick={onRefresh} className="btn-outline text-xs sm:text-sm">
              Refresh
            </button>
          </div>
        </div>
      </div>

      {/* Orders Table */}
      <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
        {filteredOrders.length === 0 ? (
          <div className="text-center py-8 sm:py-12">
            <ClipboardDocumentListIcon className="mx-auto h-10 w-10 sm:h-12 sm:w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">
              {hasFilters ? 'No orders match your filters' : 'No orders yet'}
            </h3>
            <p className="mt-1 text-xs sm:text-sm text-gray-500">
              {hasFilters ? 'Try adjusting your filters.' : 'Orders from customers will appear here.'}
            </p>
          </div>
        ) : (
          <>
            {/* Desktop Table - Hidden on mobile */}
            <div className="hidden md:block overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-3 text-left text-xs font-semibold text-gray-600">Order #</th>
                    <th className="px-3 py-3 text-left text-xs font-semibold text-gray-600">Date</th>
                    <th className="px-3 py-3 text-left text-xs font-semibold text-gray-600">Customer</th>
                    <th className="px-3 py-3 text-left text-xs font-semibold text-gray-600">Items</th>
                    <th className="px-3 py-3 text-right text-xs font-semibold text-gray-600">Amount</th>
                    <th className="px-3 py-3 text-center text-xs font-semibold text-gray-600">Payment</th>
                    <th className="px-3 py-3 text-center text-xs font-semibold text-gray-600">Status</th>
                    <th className="px-3 py-3 text-right text-xs font-semibold text-gray-600">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {pagedOrders.map((order) => (
                    <tr key={order.id} className="hover:bg-gray-50">
                      <td className="px-3 py-3 text-sm font-medium text-gray-900">
                        #{order.orderNumber}
                      </td>
                      <td className="px-3 py-3 text-sm text-gray-600">
                        {new Date(order.createdAt).toLocaleDateString()}
                        <br />
                        <span className="text-xs text-gray-400">
                          {new Date(order.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </span>
                      </td>
                      <td className="px-3 py-3 text-sm text-gray-700">
                        <div className="font-medium flex items-center gap-1">
                          {order.customerName}
                          {isGuestOrder(order) && (
                            <span className="px-1.5 py-0.5 text-[10px] bg-gray-100 text-gray-500 rounded" title="Guest customer - not registered on Omabra">
                              Guest
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-gray-500">{order.customerPhone}</div>
                      </td>
                      <td className="px-3 py-3 text-sm text-gray-600 max-w-xs">
                        <div className="truncate" title={order.items.map(i => `${i.quantity}x ${i.productName}`).join(', ')}>
                          {order.items.map((item, i) => (
                            <span key={i}>
                              {i > 0 && ', '}
                              {item.quantity}x {item.productName}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td className="px-3 py-3 text-sm font-semibold text-gray-900 text-right">
                        KES {Number(order.totalAmount).toLocaleString()}
                      </td>
                      <td className="px-3 py-3 text-center">
                        <span className={`px-2 py-1 text-xs rounded-full font-medium ${paymentColors[order.paymentStatus]}`}>
                          {order.paymentStatus === 'paid' ? '✓ Paid' : order.paymentStatus === 'pending' ? '⏳ Pending' : '✗ Failed'}
                        </span>
                      </td>
                      <td className="px-3 py-3 text-center">
                        <span className={`px-2 py-1 text-xs rounded-full font-medium capitalize ${statusColors[order.status]}`}>
                          {order.status}
                        </span>
                      </td>
                      <td className="px-3 py-3 text-right">
                        <div className="flex justify-end gap-1 flex-wrap">
                          {order.status === 'pending' && (
                            <button
                              onClick={() => handleUpdateStatus(order, 'confirmed')}
                              className="px-2 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700"
                            >
                              Confirm
                            </button>
                          )}
                          {order.status === 'confirmed' && (
                            <button
                              onClick={() => handleUpdateStatus(order, 'ready')}
                              className="px-2 py-1 text-xs bg-purple-600 text-white rounded hover:bg-purple-700"
                            >
                              Ready
                            </button>
                          )}
                          {order.status === 'ready' && order.paymentStatus === 'paid' && (
                            <button
                              onClick={() => handleUpdateStatus(order, 'shipped')}
                              className="px-2 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700"
                              title="Mark as shipped - Customer will confirm delivery"
                            >
                              Ship
                            </button>
                          )}
                          {order.status === 'ready' && order.paymentStatus !== 'paid' && (
                            <span 
                              className="px-2 py-1 text-xs bg-yellow-100 text-yellow-700 rounded cursor-help"
                              title="Cannot ship until customer pays"
                            >
                              Awaiting Payment
                            </span>
                          )}
                          {order.status === 'shipped' && isGuestOrder(order) && order.paymentStatus === 'paid' && (
                            <button
                              onClick={() => handleUpdateStatus(order, 'delivered')}
                              className="px-2 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700"
                              title="Mark as delivered - Releases payment to your wallet"
                            >
                              Delivered
                            </button>
                          )}
                          {order.status === 'shipped' && !isGuestOrder(order) && (
                            <span className="px-2 py-1 text-xs text-blue-600 italic" title="Registered customer will confirm delivery">
                              Awaiting customer
                            </span>
                          )}
                          {order.status !== 'delivered' && order.status !== 'shipped' && order.status !== 'cancelled' && (
                            <button
                              onClick={() => handleCancelOrder(order)}
                              className="px-2 py-1 text-xs border border-red-300 text-red-600 rounded hover:bg-red-50"
                              title="Cancel Order"
                            >
                              ✗
                            </button>
                          )}
                          {(order.status === 'cancelled' || order.paymentStatus !== 'paid') && (
                            <button
                              onClick={() => handleDeleteOrder(order)}
                              className="px-2 py-1 text-xs border border-gray-300 text-gray-500 rounded hover:bg-gray-100"
                              title="Delete Order"
                            >
                              🗑️
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile Cards - Shown only on mobile */}
            <div className="md:hidden divide-y divide-gray-200">
              {pagedOrders.map((order) => (
                <div key={order.id} className="p-3 sm:p-4">
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-gray-900 text-sm">#{order.orderNumber}</span>
                        <span className={`px-1.5 py-0.5 text-xs rounded-full ${paymentColors[order.paymentStatus]}`}>
                          {order.paymentStatus === 'paid' ? '✓ Paid' : '⏳'}
                        </span>
                        <span className={`px-1.5 py-0.5 text-xs rounded-full capitalize ${statusColors[order.status]}`}>
                          {order.status}
                        </span>
                      </div>
                      <p className="text-xs text-gray-500 mt-0.5">
                        {new Date(order.createdAt).toLocaleDateString()} {new Date(order.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </p>
                    </div>
                    <span className="text-sm font-bold text-gray-900">KES {Number(order.totalAmount).toLocaleString()}</span>
                  </div>
                  
                  <div className="text-xs text-gray-600 mb-1 flex items-center gap-1">
                    <span className="font-medium">{order.customerName}</span>
                    {isGuestOrder(order) && <span className="px-1 py-0.5 text-[10px] bg-gray-100 text-gray-500 rounded">Guest</span>}
                    <span>• {order.customerPhone}</span>
                  </div>
                  
                  <div className="text-xs text-gray-500 mb-2 line-clamp-1">
                    {order.items.map((item, i) => (
                      <span key={i}>{i > 0 && ', '}{item.quantity}x {item.productName}</span>
                    ))}
                  </div>

                  <div className="flex gap-1.5 flex-wrap">
                    {order.status === 'pending' && (
                      <button onClick={() => handleUpdateStatus(order, 'confirmed')} className="px-2 py-1 text-xs bg-blue-600 text-white rounded">
                        Confirm
                      </button>
                    )}
                    {order.status === 'confirmed' && (
                      <button onClick={() => handleUpdateStatus(order, 'ready')} className="px-2 py-1 text-xs bg-purple-600 text-white rounded">
                        Ready
                      </button>
                    )}
                    {order.status === 'ready' && order.paymentStatus === 'paid' && (
                      <button onClick={() => handleUpdateStatus(order, 'shipped')} className="px-2 py-1 text-xs bg-blue-600 text-white rounded" title="Customer will confirm delivery">
                        Ship
                      </button>
                    )}
                    {order.status === 'ready' && order.paymentStatus !== 'paid' && (
                      <span className="px-2 py-1 text-xs bg-yellow-100 text-yellow-700 rounded">
                        Awaiting Payment
                      </span>
                    )}
                    {order.status === 'shipped' && isGuestOrder(order) && order.paymentStatus === 'paid' && (
                      <button onClick={() => handleUpdateStatus(order, 'delivered')} className="px-2 py-1 text-xs bg-green-600 text-white rounded" title="Releases payment to wallet">
                        Delivered
                      </button>
                    )}
                    {order.status === 'shipped' && !isGuestOrder(order) && (
                      <span className="px-2 py-1 text-xs text-blue-600 italic">Awaiting customer</span>
                    )}
                    {order.status !== 'delivered' && order.status !== 'shipped' && order.status !== 'cancelled' && (
                      <button onClick={() => handleCancelOrder(order)} className="px-2 py-1 text-xs border border-red-300 text-red-600 rounded">
                        Cancel
                      </button>
                    )}
                    {(order.status === 'cancelled' || order.paymentStatus !== 'paid') && (
                      <button onClick={() => handleDeleteOrder(order)} className="px-2 py-1 text-xs border border-gray-300 text-gray-500 rounded">
                        🗑️
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination */}
            <div className="flex flex-col sm:flex-row items-center justify-between gap-3 px-4 py-3 border-t bg-gray-50">
              <span className="text-sm text-gray-600">
                Showing {Math.min((page - 1) * pageSize + 1, filteredOrders.length)} - {Math.min(page * pageSize, filteredOrders.length)} of {filteredOrders.length} orders
              </span>
              <div className="flex items-center gap-2">
                <select
                  className="input py-1 px-2 text-sm"
                  value={pageSize}
                  onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}
                >
                  {[10, 20, 50, 100].map(s => <option key={s} value={s}>{s}/page</option>)}
                </select>
                <button
                  className="btn-outline text-sm py-1 px-2"
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page <= 1}
                >
                  Prev
                </button>
                <span className="text-sm text-gray-600">Page {page} of {totalPages || 1}</span>
                <button
                  className="btn-outline text-sm py-1 px-2"
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page >= totalPages}
                >
                  Next
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Shop;

